// CU-86d45b2zn — the read-only customer order history over commerce's customer-safe orders endpoint.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Result, Space, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useQuery } from "@tanstack/react-query";

import CustomerLayout from "../../components/customer/CustomerLayout";
import { DataTable, EmptyState, InlineLoading, PageContainer, SectionCard, StatusTag } from "../../components/shared";
import { classifyCustomerAccessError } from "../../api/customer/customerAccess";
import { fetchCustomerOrders } from "../../api/customer/customerOrdersApi";
import type { CustomerOrder, CustomerOrderItem } from "../../api/customer/customerOrdersApi";
import { formatPrice } from "./catalogPresentation";
import { formatOrderDate, fulfilmentStatus } from "./ordersPresentation";

const { Text } = Typography;

export const ORDERS_PAGE_SIZE = 10;

const dash = <Text type="secondary">—</Text>;

const statusOrDash = (status: string | null) => (status ? <StatusTag status={status} /> : dash);

const itemColumns: ColumnsType<CustomerOrderItem> = [
	{ title: "Product", key: "product", render: (_, item) => <Text>{item.product_name ?? "Product"}</Text> },
	{ title: "Option", key: "variant", width: 160, render: (_, item) => (item.variant_title ? <Text type="secondary">{item.variant_title}</Text> : dash) },
	{ title: "Qty", dataIndex: "quantity", key: "quantity", width: 70, align: "right" },
	{ title: "Unit price", key: "unit_price", width: 110, align: "right", render: (_, item) => formatPrice(item.unit_price) },
	{ title: "Total", key: "line_total", width: 110, align: "right", render: (_, item) => formatPrice(item.line_total) },
];

const OrderDetail: React.FC<{ order: CustomerOrder }> = ({ order }) => (
	<div data-testid="order-detail">
		<DataTable<CustomerOrderItem> bare columns={itemColumns} dataSource={order.items} rowKey="item_id" pagination={false} minHeight={0} />
		<Space direction="vertical" size={2} style={{ width: "100%", alignItems: "flex-end", marginTop: 12 }}>
			{order.shipping_total !== null && (
				<Text data-testid="order-detail-shipping">
					<Text type="secondary">Shipping:</Text> {formatPrice(order.shipping_total)}
				</Text>
			)}
			{order.tax_total !== null && (
				<Text data-testid="order-detail-tax">
					<Text type="secondary">Tax:</Text> {formatPrice(order.tax_total)}
				</Text>
			)}
			<Text strong data-testid="order-detail-total">
				<Text type="secondary">Total:</Text> {formatPrice(order.total_amount)}
			</Text>
		</Space>
	</div>
);

const CustomerOrdersPage: React.FC = () => {
	const navigate = useNavigate();
	const [page, setPage] = useState(1);
	const [selected, setSelected] = useState<CustomerOrder | null>(null);

	const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
		queryKey: ["customer-orders", page],
		queryFn: () => fetchCustomerOrders({ page, perPage: ORDERS_PAGE_SIZE }),
		// CU-86d45b2zn: no automatic retry; a 403 is a decision and each outcome renders its own action.
		retry: false,
	});

	const outcome = isError ? classifyCustomerAccessError(error) : null;

	const columns: ColumnsType<CustomerOrder> = [
		{
			title: "Order",
			key: "reference",
			render: (_, order) => (
				<Text strong data-testid={`order-ref-${order.order_id}`}>
					{order.reference}
				</Text>
			),
		},
		{
			title: "Date",
			key: "order_date",
			render: (_, order) => <Text data-testid={`order-date-${order.order_id}`}>{formatOrderDate(order.order_date)}</Text>,
		},
		{
			title: "Status",
			key: "status",
			render: (_, order) => <span data-testid={`order-status-${order.order_id}`}>{statusOrDash(order.status)}</span>,
		},
		{
			title: "Payment",
			key: "payment",
			render: (_, order) => <span data-testid={`order-payment-${order.order_id}`}>{statusOrDash(order.financial_status)}</span>,
		},
		{
			title: "Fulfilment",
			key: "fulfilment",
			render: (_, order) => <span data-testid={`order-fulfilment-${order.order_id}`}>{statusOrDash(fulfilmentStatus(order))}</span>,
		},
		{
			title: "Items",
			key: "items",
			align: "right",
			render: (_, order) => <Text data-testid={`order-items-${order.order_id}`}>{order.items.length}</Text>,
		},
		{
			title: "Total",
			key: "total",
			align: "right",
			render: (_, order) => (
				<Text strong data-testid={`order-total-${order.order_id}`}>
					{formatPrice(order.total_amount)}
				</Text>
			),
		},
		{
			title: "",
			key: "view",
			width: 90,
			render: (_, order) => (
				<Button type="link" size="small" onClick={() => setSelected(order)} data-testid={`order-view-${order.order_id}`}>
					View
				</Button>
			),
		},
	];

	const renderBody = () => {
		if (isLoading) return <InlineLoading message="Loading orders…" minHeight={300} />;

		if (outcome === "session_refresh_required") {
			return (
				<div data-testid="orders-refresh-required">
					<Result
						status="warning"
						title="Please sign in again"
						subTitle="Your session was started before customer access was enabled, or it has expired. Sign in again to see your orders."
						extra={
							<Button type="primary" onClick={() => navigate("/logout")} data-testid="orders-sign-in-again">
								Sign in again
							</Button>
						}
					/>
				</div>
			);
		}

		if (outcome === "forbidden") {
			return (
				<div data-testid="orders-forbidden">
					<Result
						status="403"
						title="Customer access is not available for this account"
						subTitle="This account is not part of the customer directory. If you believe it should be, please contact the clinic."
					/>
				</div>
			);
		}

		if (outcome === "unavailable") {
			return (
				<div data-testid="orders-error">
					<Result
						status="error"
						title="We couldn't load your orders"
						subTitle="Something went wrong on our side. Please try again."
						extra={
							<Button onClick={() => refetch()} loading={isFetching} data-testid="orders-retry">
								Try again
							</Button>
						}
					/>
				</div>
			);
		}

		if (!data || data.data.length === 0) {
			return <EmptyState title="No orders yet" description="When you place an order with the clinic, it will appear here." />;
		}

		return (
			<SectionCard>
				<DataTable<CustomerOrder>
					columns={columns}
					dataSource={data.data}
					rowKey="order_id"
					onRowClick={(order) => setSelected(order)}
					pagination={{
						current: data.pagination.current_page,
						pageSize: data.pagination.per_page,
						total: data.pagination.total_items,
						showSizeChanger: false,
						onChange: (next) => setPage(next),
					}}
					data-testid="orders-table"
				/>
			</SectionCard>
		);
	};

	return (
		<CustomerLayout>
			<PageContainer title="Orders" subtitle="Your order history with the clinic" maxWidth={1280}>
				{renderBody()}
			</PageContainer>

			<Modal
				open={!!selected}
				onCancel={() => setSelected(null)}
				destroyOnClose
				width={760}
				styles={{ body: { maxHeight: "70vh", overflow: "auto" } }}
				title={selected ? `Order ${selected.reference}` : ""}
				footer={[
					<Button key="close" type="primary" onClick={() => setSelected(null)} data-testid="order-detail-close">
						Close
					</Button>,
				]}
			>
				{selected && <OrderDetail order={selected} />}
			</Modal>
		</CustomerLayout>
	);
};

export default CustomerOrdersPage;
